// broadcast.ts — FACE & FURY STRETCH, Lane A (BROADCAST PACKAGE). Makes the rival read as a live
// BROADCAST OPPONENT, not a text line, with three renderer-only surfaces that are PURE CONSUMERS of the
// sim Snapshot + the renderer's own match structure — never Core, never AI internals:
//
//   A1  LOWER-THIRD BOUT FRAMING — a brief "YOU vs BYT3 · <TIER>" strap that establishes the bout on
//       the rival's JOIN board, then slides away. Once per match (registry once-guard), never on
//       warmup / solo / novice board 0-1.
//   A2  RIVAL PRESSURE — a persistent-but-quiet standing meter. HARD RULE: a PURE function of Snapshot
//       observables ONLY (score margin, quota-pace differential, time left, boards remaining). It reads
//       players[0] and players[1] PUBLIC fields — the SAME fields a future remote player exposes — so it
//       is PvP-symmetric by construction and reads ZERO AI internals (no knowledgeAccuracy/belief/
//       AIController). We deliberately ship a STANDING+MOMENTUM meter, NOT a fake win-%: an honestly
//       calibrated win-probability isn't defensible from these four observables alone, so RIVAL PRESSURE
//       shows relative standing (a tug-of-war) + which way it's trending, and never claims a percentage.
//   A3  #44 MENACE BEAT — when BYT3 TAKES or EXTENDS a meaningful lead: a subtle screen-edge villain-tint
//       pulse (the rival IDENTITY channel — magenta, never raw hazard-red) + a gloat pop on his HUD
//       portrait, throttled to ≤1 per window. ONE FOCAL POINT: while the gloat portrait owns the beat we
//       DIM the grid-token highlight (aiPressureCue) so the eye never reads two rivals emoting at once.
//       Eyes+ears agree: the menace lead threshold sits AT/ABOVE the villain-bassline audio deadzone
//       (musicDirector.ts VILLAIN_LEAD_DEADZONE), so a menace beat can never fire while the villain layer
//       is silent, and the beat re-fires BYT3's persona gloat/bark (audible villain) on an EXTEND.
//
//   A4  Legibility survives the juice: the edge tint frames the OUTER screen at a low HUD-safe depth
//       (board centre + telemetry stay fully readable), colourblind mode is respected (rival channel +
//       shape/label double-coding, not colour alone), reduceMotion swaps every pulse for a static hold,
//       and every font size flows through scaledFont so 100%/150% text scale both fit.
import Phaser from 'phaser';
import type { Snapshot, AiTierName } from '../../core';
import { PALETTE, hex, FONT, tex, hasTex, KEYS } from '../keys';
import { motionAllowed, scaledFont } from '../settings';
import { isLandscape, LANDSCAPE_GUTTER_W } from '../stage';
import { RIVAL_NAME } from '../rivalPersona';
import { PLAYER, AI } from './types';
import type { GameScene } from '../scenes/GameScene';

// =============================================================================
// CONFIG — every threshold / throttle / weight lives here (never inline). Renderer-only tunables; Core
// config is untouchable. Kept in one greppable block so balance can re-tune the broadcast package without
// hunting through function bodies.
// =============================================================================
export const BROADCAST = {
  // -- A1 lower-third bout intro --
  boutIntroDelayMs: 560, // wait past round-start so the strap never collides with the topic flash
  boutIntroInMs: 400,
  boutIntroHoldMs: 2500, // on-screen dwell, then it gets out of the way
  boutIntroOutMs: 300,
  // Lower-third anchor (fraction of viewport height). Nudged down toward the board's lower band so it sits
  // over the least-contested rows; the board fills nearly the whole landscape height, so it cannot clear
  // the tiles vertically there — the plate is semi-transparent (below) so live tiles GHOST THROUGH instead
  // of being occluded, in both viewports.
  boutIntroYFrac: 0.66,
  boutIntroPlateAlpha: 0.55, // strap plate fill opacity — < 1 so covered board tiles ghost through, never hidden

  // -- A2 RIVAL PRESSURE standing meter (PURE Snapshot observables) --
  // Standing S ∈ [-1,+1]: + = human favoured, − = rival favoured. Blended from two observable terms,
  // each already normalised to [-1,1], then time- and match-context-weighted. Weights sum to 1.
  pressureScoreSpan: 400, // score-margin (pts) that saturates the score term (~4 corrects)
  pressureWeightScore: 0.6,
  pressureWeightQuota: 0.4,
  pressureTimeGamma: 0.5, // time-left sharpening: the standing tightens toward its sign as the clock drains
  pressureMatchGamma: 0.12, // later boards amplify the standing a touch (board 0 → ×1.0, board 4 → ×1.12)
  matchBoards: 5, // boards in a full match (for the boards-remaining observable)
  pressureSmoothing: 0.14, // per-frame lerp toward the target standing (presentation easing only)
  pressureRedrawEps: 0.006, // dirty-check: only repaint the bar when the standing moved this much
  momentumWindowMs: 1300, // window over which momentum (trend) is measured
  momentumMinDelta: 0.07, // ignore standing jitter below this — the caret only shows a real swing

  // -- A3 #44 menace beat --
  menaceTakeMargin: 80, // rival lead (pts) needed for a "took the lead" beat — ≥ villain audio deadzone (30)
  menaceExtendBands: [200, 360] as const, // rival lead crossing UP through these (pts) = an "extend" beat
  menaceThrottleMs: 6000, // ≤1 menace beat per this window
  menaceGloatMs: 1500, // gloat-portrait / focal-suppression window
  menaceEdgePeakAlpha: 0.24, // subtle magenta edge-frame peak (motion pulse)
  menaceEdgeStaticAlpha: 0.16, // reduceMotion held-edge alpha (no pulse)
  menaceFocalDimAlpha: 0.12, // aiPressureCue alpha while the gloat portrait owns the focus
  wrongFocalMs: 1200, // ruling 6: RIVAL WRONG schadenfreude beat owns the focus this long (grid cue dims)
} as const;

/** Per-match once-guard for the bout intro (registry, like the persona grudge cold-open). */
const BOUT_INTRO_KEY = 'broadcast.boutIntroShown';

/** Re-arm the bout intro for a new match. Call once at match start (GameScene.init, board 0). */
export function resetBoutIntro(scene: Phaser.Scene): void {
  scene.registry.set(BOUT_INTRO_KEY, false);
}

/** Broadcast-friendly tier label for the lower-third (Core tier name → an UPPERCASE broadcast word). */
const TIER_LABEL: Record<AiTierName, string> = {
  rookie: 'ROOKIE',
  sharp: 'SHARP',
  savant: 'SAVANT',
};

/**
 * The broadcast HUD package. ONE instance per board (rebuilt in GameScene.create like RivalPersona), and
 * ONLY constructed for a BYT3 match (aiEnabled && no ladder ghost) so the whole package is scoped to the
 * live rival — solo/novice-early/ladder surfaces never see it. All state is encapsulated here so GameScene
 * gains exactly one field.
 */
export class Broadcast {
  private scene: GameScene;

  // A2 pressure bar objects (built once per board, die with the scene).
  private barFill?: Phaser.GameObjects.Graphics; // diverging fill + centre tick (redrawn on change)
  private barCaret?: Phaser.GameObjects.Graphics; // momentum caret (redrawn on change)
  private barW = 140;
  private barH = 7;
  private standing = 0; // smoothed [-1,+1], + = human ahead
  private lastDrawnStanding = 99; // force a first paint
  private lastCaretDir = 0; // -1 rival gaining / +1 human gaining / 0 none
  private hist: { t: number; s: number }[] = []; // standing samples for momentum

  // A3 menace objects/state.
  private edge?: Phaser.GameObjects.Graphics; // screen-edge villain tint frame (low depth, HUD-safe)
  private edgeVW = 0;
  private edgeVH = 0;
  private lastMenaceMs = -Infinity;
  private focalUntilMs = 0; // gloat portrait owns the focus until this scene-time
  private cueSuppressed = false; // edge-trigger for the one-focal-point aiPressureCue dim
  private prevRivalLead = 0; // for extend-band crossing detection
  private leadSeeded = false;

  constructor(scene: GameScene) {
    this.scene = scene;
  }

  /** Build the persistent pressure bar + the (hidden) menace edge frame. Call once, after buildHud. */
  build(snap: Snapshot): void {
    this.buildEdge();
    this.buildPressureBar();
    this.seedLead(snap);
    this.updatePressure(snap, true);
  }

  // --- A2 RIVAL PRESSURE -----------------------------------------------------

  /**
   * The PURE standing function. Reads ONLY Snapshot observables + match structure — the exact surface a
   * remote player exposes, so this is PvP-symmetric verbatim and never touches AI internals:
   *   • score margin        (players[i].score)
   *   • quota-pace diff      (players[i].eatenCorrect vs snap.quota)
   *   • time remaining       (snap.timeLeftMs vs the board's timerMax)
   *   • boards remaining     (scene.boardIndex within a MATCH of BROADCAST.matchBoards)
   * Returns S ∈ [-1,+1]; + = human favoured, − = rival favoured. No RNG, no Date, no sim writes.
   */
  private computeStanding(snap: Snapshot): number {
    const human = snap.players.find((p) => p.id === PLAYER);
    const rival = snap.players.find((p) => p.id === AI);
    if (!human || !rival) return 0;
    // Score-margin term.
    const scoreTerm = clampUnit((human.score - rival.score) / BROADCAST.pressureScoreSpan);
    // Quota-pace differential (whoever is closer to the END trigger is applying pressure).
    const quota = Math.max(1, snap.quota);
    const quotaTerm = clampUnit((human.eatenCorrect - rival.eatenCorrect) / quota);
    let s = BROADCAST.pressureWeightScore * scoreTerm + BROADCAST.pressureWeightQuota * quotaTerm;
    // Time-left sharpening: as the clock drains the current standing "locks in" (the gap matters more).
    const timeFrac = clampUnit(snap.timeLeftMs / Math.max(1, this.scene.timerMaxMs), 0, 1);
    s *= 1 + BROADCAST.pressureTimeGamma * (1 - timeFrac);
    // Boards-remaining: later boards of a match carry higher stakes (a mild amplitude; ×1 on board 0).
    const boardFrac = clampUnit(this.scene.boardIndex / Math.max(1, BROADCAST.matchBoards - 1), 0, 1);
    s *= 1 + BROADCAST.pressureMatchGamma * boardFrac;
    return clampUnit(s);
  }

  private buildPressureBar(): void {
    const land = isLandscape();
    const { x, y } = this.barAnchor();
    this.barW = land ? LANDSCAPE_GUTTER_W - 48 : 116;
    const c = this.scene.add.container(x, y).setDepth(2).setScrollFactor(0);
    // Eyebrow — the label carries the meaning so the bar is never colour-only (colourblind-safe).
    const eyebrow = this.scene.add
      .text(0, -this.barH / 2 - 8, 'RIVAL PRESSURE', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(9, 9)}px`,
        color: hex(PALETTE.dim),
        fontStyle: 'bold',
      })
      .setOrigin(0, 1);
    // End-cap identity labels (double-code the tug direction beyond colour): YOU on the left, BYT3 ▸ right.
    const capFs = scaledFont(10, 10);
    const youCap = this.scene.add
      .text(0, this.barH / 2 + 3, 'YOU', { fontFamily: FONT.display, fontSize: `${capFs}px`, color: hex(PALETTE.player), fontStyle: 'bold' })
      .setOrigin(0, 0);
    const rivalCap = this.scene.add
      .text(this.barW, this.barH / 2 + 3, RIVAL_NAME, { fontFamily: FONT.display, fontSize: `${capFs}px`, color: hex(PALETTE.ai), fontStyle: 'bold' })
      .setOrigin(1, 0);
    // Static track (drawn once); the diverging fill + centre tick live on their own Graphics (redrawn).
    const track = this.scene.add.graphics();
    track.fillStyle(PALETTE.surface, 0.9);
    track.fillRoundedRect(0, -this.barH / 2, this.barW, this.barH, this.barH / 2);
    track.lineStyle(1, PALETTE.lineStrong, 0.7);
    track.strokeRoundedRect(0, -this.barH / 2, this.barW, this.barH, this.barH / 2);
    this.barFill = this.scene.add.graphics();
    this.barCaret = this.scene.add.graphics();
    c.add([track, this.barFill, this.barCaret, eyebrow, youCap, rivalCap]);
    // The container (and all children) are destroyed with the scene on board change; no manual teardown.
  }

  /** Where the bar sits: grouped with the rival readout (right in portrait, left rail in landscape). */
  private barAnchor(): { x: number; y: number } {
    const W = this.scene.scale.width;
    if (isLandscape()) {
      const railTop = 118;
      return { x: 24, y: railTop + 300 }; // between the rival readout row (+262) and hearts (+330)
    }
    // Portrait: below the larger BYT3 score/hearts stack, tucked to the right so it never cuts through
    // the rival score or the centered win-status pill.
    const barW = 116;
    const rightX = W - Math.max(16, W * 0.06);
    const scale = scaledFont(10, 10) / 10;
    return { x: rightX - barW, y: scale >= 1.25 ? 194 : 176 };
  }

  /**
   * Per-frame pressure update: ease the standing toward its pure target, refresh the momentum trend, and
   * repaint the diverging tug-of-war (only on a real change). `immediate` snaps (create-time paint).
   */
  updatePressure(snap: Snapshot, immediate = false): void {
    if (!this.barFill || !this.barCaret) return;
    const target = this.computeStanding(snap);
    this.standing = immediate ? target : this.standing + (target - this.standing) * BROADCAST.pressureSmoothing;
    // Momentum: compare the current standing to the oldest sample inside the window.
    const now = this.scene.time.now;
    this.hist.push({ t: now, s: this.standing });
    while (this.hist.length > 0 && now - this.hist[0].t > BROADCAST.momentumWindowMs) this.hist.shift();
    const oldest = this.hist[0]?.s ?? this.standing;
    const momentum = this.standing - oldest;
    const caretDir = Math.abs(momentum) < BROADCAST.momentumMinDelta ? 0 : momentum > 0 ? 1 : -1;
    // Dirty-check so we don't rebuild graphics every frame.
    if (!immediate && Math.abs(this.standing - this.lastDrawnStanding) < BROADCAST.pressureRedrawEps && caretDir === this.lastCaretDir) {
      return;
    }
    this.lastDrawnStanding = this.standing;
    this.lastCaretDir = caretDir;
    this.paintBar(caretDir);
  }

  private paintBar(caretDir: number): void {
    const fill = this.barFill;
    const caret = this.barCaret;
    if (!fill || !caret) return;
    const half = this.barW / 2;
    const h = this.barH;
    const inset = 1; // keep the fill just inside the track stroke
    fill.clear();
    // Diverging tug-of-war from the centre: human favoured → cyan grows LEFT; rival favoured → magenta
    // grows RIGHT. `standing` +1 = full human, −1 = full rival.
    const s = this.standing;
    if (s > 0) {
      const len = s * (half - inset);
      fill.fillStyle(PALETTE.player, 0.92);
      fill.fillRoundedRect(half - len, -h / 2 + inset, len, h - inset * 2, (h - inset * 2) / 2);
    } else if (s < 0) {
      const len = -s * (half - inset);
      fill.fillStyle(PALETTE.ai, 0.92);
      fill.fillRoundedRect(half, -h / 2 + inset, len, h - inset * 2, (h - inset * 2) / 2);
    }
    // Centre tick (the even line) — always visible so "who's ahead" is read against a fixed midpoint.
    fill.fillStyle(PALETTE.text, 0.85);
    fill.fillRect(half - 1, -h / 2 - 2, 2, h + 4);
    // Momentum caret: a small triangle above the bar on the GAINING side (colour = that side). Static
    // shape, so it is reduceMotion- and colourblind-safe (position + colour + the end-cap labels).
    caret.clear();
    if (caretDir !== 0) {
      const gainingRival = caretDir < 0;
      const cx = gainingRival ? this.barW * 0.72 : this.barW * 0.28;
      const cy = -h / 2 - 5;
      const col = gainingRival ? PALETTE.ai : PALETTE.player;
      caret.fillStyle(col, 0.95);
      // caret points toward its side's edge (rival → right, human → left)
      if (gainingRival) caret.fillTriangle(cx - 4, cy - 3, cx - 4, cy + 3, cx + 2, cy);
      else caret.fillTriangle(cx + 4, cy - 3, cx + 4, cy + 3, cx - 2, cy);
    }
  }

  // --- A3 #44 MENACE BEAT ----------------------------------------------------

  private buildEdge(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    this.edgeVW = Math.round(W * 0.14);
    this.edgeVH = Math.round(H * 0.15);
    // A magenta (rival IDENTITY channel) edge FRAME at a low, HUD-safe depth so the board centre and all
    // telemetry stay fully readable — only the outer screen margins tint. Mirrors tensionVeil's edge-frame
    // idea but in the rival channel, never hazard-red. Hidden until a menace beat fires.
    const g = this.scene.add.graphics().setDepth(0.6).setScrollFactor(0).setVisible(false).setAlpha(0);
    const col = PALETTE.ai;
    const vw = this.edgeVW;
    const vh = this.edgeVH;
    g.fillGradientStyle(col, col, col, col, 0.28, 0.28, 0, 0);
    g.fillRect(0, 0, W, vh); // top (kept weak — HUD telemetry lives here)
    g.fillGradientStyle(col, col, col, col, 0, 0, 0.8, 0.8);
    g.fillRect(0, H - vh, W, vh); // bottom
    g.fillGradientStyle(col, col, col, col, 0.72, 0, 0.72, 0);
    g.fillRect(0, 0, vw, H); // left
    g.fillGradientStyle(col, col, col, col, 0, 0.72, 0, 0.72);
    g.fillRect(W - vw, 0, vw, H); // right
    this.edge = g;
  }

  private seedLead(snap: Snapshot): void {
    const human = snap.players.find((p) => p.id === PLAYER);
    const rival = snap.players.find((p) => p.id === AI);
    this.prevRivalLead = human && rival ? rival.score - human.score : 0;
    this.leadSeeded = true;
  }

  /**
   * Called from the HUD's lead-flip seam (updateRivalReadout) when the standings FLIP. sign<0 = the rival
   * just TOOK the lead. The persona already barks on the flip (reactLeadChange in the same block), so this
   * fires only the VISUAL broadcast beat (no double-bark).
   */
  onLeadFlip(snap: Snapshot, sign: number, lead: number): void {
    if (sign < 0 && -lead >= BROADCAST.menaceTakeMargin) this.fireMenace(snap, false);
  }

  /**
   * Per-frame: ease the pressure bar, decay the focal window, and detect an EXTEND — the rival was already
   * ahead and pushed his lead UP through a new danger band (no flip fires here, so the bar catches it).
   */
  update(snap: Snapshot): void {
    this.updatePressure(snap);
    this.applyFocalSuppression();
    if (snap.status !== 'playing') {
      this.prevRivalLead = this.currentRivalLead(snap);
      return;
    }
    const lead = this.currentRivalLead(snap); // + = rival ahead
    if (this.leadSeeded) {
      for (const band of BROADCAST.menaceExtendBands) {
        // crossed UP through a band this frame (and still throttle-eligible) → an EXTEND menace beat.
        if (this.prevRivalLead < band && lead >= band) {
          this.fireMenace(snap, true);
          break;
        }
      }
    }
    this.prevRivalLead = lead;
  }

  private currentRivalLead(snap: Snapshot): number {
    const human = snap.players.find((p) => p.id === PLAYER);
    const rival = snap.players.find((p) => p.id === AI);
    return human && rival ? rival.score - human.score : 0;
  }

  /**
   * The #44 beat. `fromExtend` = a band-crossing with no flip, so we ALSO re-fire BYT3's persona gloat/bark
   * (a flip already routed through reactLeadChange). Throttled so takes+extends never stack. Eyes: edge
   * pulse + portrait gloat. Ears: the villain bassline is already swelling (musicDirector drives it off the
   * SAME margin, and menaceTakeMargin ≥ the villain deadzone, so it is provably non-silent here) plus the
   * re-fired gloat voice on an extend. One focal point: the gloat portrait owns the focus → aiPressureCue
   * is dimmed for the window (applyFocalSuppression).
   */
  private fireMenace(snap: Snapshot, fromExtend: boolean): void {
    const now = this.scene.time.now;
    if (now - this.lastMenaceMs < BROADCAST.menaceThrottleMs) return;
    this.lastMenaceMs = now;
    this.focalUntilMs = now + BROADCAST.menaceGloatMs;
    this.pulseEdge();
    this.gloatPortrait();
    if (fromExtend) {
      // No flip fired the persona this time → give BYT3 his gloat + "Too slow!" so ears match the eyes.
      const lead = this.currentRivalLead(snap);
      this.scene.rivalPersona?.reactLeadChange(-1, -lead);
    }
  }

  private pulseEdge(): void {
    const g = this.edge;
    if (!g) return;
    this.scene.tweens.killTweensOf(g);
    g.setVisible(true);
    if (!motionAllowed()) {
      // reduceMotion: a STATIC held edge vignette (no pulse), then a slow fade — a calm cue, never a strobe.
      g.setAlpha(BROADCAST.menaceEdgeStaticAlpha);
      this.scene.tweens.add({
        targets: g,
        alpha: 0,
        delay: 520,
        duration: 360,
        ease: 'Sine.easeIn',
        onComplete: () => g.setVisible(false),
      });
      return;
    }
    g.setAlpha(0);
    this.scene.tweens.add({
      targets: g,
      alpha: { from: 0, to: BROADCAST.menaceEdgePeakAlpha },
      duration: 200,
      ease: 'Sine.easeOut',
      yoyo: true,
      hold: 120,
      onComplete: () => g.setVisible(false),
    });
  }

  /**
   * The gloat portrait: pop the rival HUD readout + a magenta glow ring behind it, and (when the persona
   * emote portraits decoded) show the gloat expression. This is the FOCAL point of the beat — while it is
   * active, the grid-token highlight (aiPressureCue) is suppressed (applyFocalSuppression) so the screen
   * never reads as two rivals emoting.
   */
  private gloatPortrait(): void {
    const readout = this.scene.rivalReadout;
    if (!readout || !readout.active) return;
    // The persona's own gloat EMOTE is driven by reactLeadChange (the flip path already called it; the
    // extend path calls it in fireMenace), so the pose-creature chip / emote portrait reacts there. This
    // method owns the always-present VISUAL tell: a readout pop + a magenta glow flare behind the chip.
    if (!motionAllowed()) return;
    // Magenta glow ring behind the readout, quickly blooming out and fading — the "portrait flares" beat.
    const b = readout.getBounds();
    const glow = this.scene.add
      .circle(b.centerX, b.centerY, Math.max(b.width, b.height) * 0.62, PALETTE.ai, 0.5)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setScrollFactor(0)
      .setDepth(1.6);
    this.scene.tweens.add({
      targets: glow,
      scale: 1.9,
      alpha: 0,
      duration: 520,
      ease: 'Quad.easeOut',
      onComplete: () => glow.destroy(),
    });
    this.scene.tweens.killTweensOf(readout);
    readout.setScale(1);
    this.scene.tweens.add({
      targets: readout,
      scale: { from: 1.22, to: 1 },
      duration: 360,
      ease: 'Back.easeOut',
      onComplete: () => readout.active && readout.setScale(1),
    });
  }

  /**
   * ONE FOCAL POINT: while the gloat portrait owns the beat, dim the grid-token highlight (aiPressureCue —
   * the magenta ring + intent projection around the rival's board token) so the eye tracks the ONE gloating
   * rival, not two. updateAiPressureCue redraws that Graphics every frame but never touches its object
   * alpha, so modulating alpha here is safe and is restored the instant the focal window ends. Applies
   * under reduceMotion too (this is a legibility rule, not a motion effect) — snapped, not tweened.
   */
  private applyFocalSuppression(): void {
    const cue = this.scene.aiPressureCue;
    if (!cue) return;
    const active = this.scene.time.now < this.focalUntilMs;
    // EDGE-TRIGGERED: only (re)start the dim/restore on a real on↔off transition. Running it every frame
    // would kill+restart a fresh tween each frame (a Zeno stall that never reaches the target) — the
    // aiCueAlpha=1-under-focus bug the first capture caught.
    if (active === this.cueSuppressed) return;
    this.cueSuppressed = active;
    const wantAlpha = active ? BROADCAST.menaceFocalDimAlpha : 1;
    this.scene.tweens.killTweensOf(cue);
    if (!motionAllowed()) {
      cue.setAlpha(wantAlpha);
      return;
    }
    this.scene.tweens.add({ targets: cue, alpha: wantAlpha, duration: active ? 160 : 260, ease: 'Sine.easeInOut' });
  }

  /**
   * S9 FIX-A (ruling 6 — "hand off, don't stack"): open a focal-suppression window from an EXTERNAL
   * beat. The RIVAL WRONG schadenfreude beat (unitsView.showRivalMunchBeat) owns the focus for its
   * duration, so the grid rival-token cue (aiPressureCue) must step aside for the same window — exactly
   * as the #44 menace gloat already does. REUSE that machinery instead of a second mechanism: extend
   * focalUntilMs so the per-frame applyFocalSuppression() dims the cue now and restores it on the
   * trailing edge. Math.max so this never SHORTENS an already-longer menace window. No-op-safe when the
   * cue isn't ready (applyFocalSuppression guards it). Under reduceMotion the dim is snapped, not tweened.
   */
  focalSuppressForWrongBeat(): void {
    this.focalUntilMs = Math.max(this.focalUntilMs, this.scene.time.now + BROADCAST.wrongFocalMs);
  }

  /** True while the gloat portrait owns the focus (evidence hook + potential external consumers). */
  focalGloatActive(): boolean {
    return this.scene.time.now < this.focalUntilMs;
  }

  /** The current smoothed standing S ∈ [-1,+1] (+ = human favoured). Read-only evidence/debug hook. */
  standingValue(): number {
    return this.standing;
  }

  // --- A1 LOWER-THIRD BOUT FRAMING -------------------------------------------

  /**
   * Show the bout intro strap on the rival's JOIN board. ONE per match via the registry once-guard (reset
   * at match start), so boards 2-4 never re-fire it. Reschedules ONCE if the pre-round coach is still up
   * (so it never renders under the coach veil). Gated off novice board 0-1 (BYT3 joins novice at board 2);
   * the Broadcast instance only exists for a non-ladder rival match, so solo/ladder never reach here.
   */
  tryShowBoutIntro(tier: AiTierName, attempt = 0): void {
    const sc = this.scene;
    if (!sc.scene.isActive() || sc.ended) return;
    if (sc.registry.get(BOUT_INTRO_KEY) === true) return;
    if (sc.noviceRun && sc.boardIndex <= 1) return; // never during novice teaching boards 0-1
    if (sc.preRoundActive) {
      if (attempt < 4) sc.time.delayedCall(500, () => this.tryShowBoutIntro(tier, attempt + 1));
      return;
    }
    sc.registry.set(BOUT_INTRO_KEY, true);
    this.buildBoutIntro(tier);
  }

  private buildBoutIntro(tier: AiTierName): void {
    const sc = this.scene;
    const W = sc.scale.width;
    const H = sc.scale.height;
    const land = isLandscape();
    let y = H * BROADCAST.boutIntroYFrac;
    let cx = W / 2;
    let strapW = Math.min(W * 0.86, 430);
    const strapH = land ? 58 : Math.max(34, scaledFont(14, 12) * 1.95);
    // S9 FIX-D (G1 AMATEUR 3b): on the LANDSCAPE stage the board fills nearly the full height, so the
    // 0.66 yFrac anchor landed the strap MID ROW-4 (800h) — the strap text sat directly on that row's
    // answer labels for the whole 2.5s hold. Re-anchor the strap's centre onto the nearest horizontal
    // ROW SEAM (the boundary between two tile rows): the 58px strap then straddles two rows' EDGES and
    // stays clear of both rows' CENTRED labels (each a full cellH/2 away). Derived from live board
    // geometry so any grid size / daily 6-row board self-corrects.
    if (land && sc.cellH > 0 && sc.rows > 1) {
      // Revamp still gate 2026-07-06: even a seam-anchored strap reads as board occlusion at 150%
      // text scale. In landscape, use the empty left broadcast rail instead of the playable tile field.
      strapW = Math.min(Math.max(244, LANDSCAPE_GUTTER_W - 28), 286);
      cx = LANDSCAPE_GUTTER_W / 2;
      // Keep the intro below the telemetry stack rather than tying it to board geometry. The rail rows
      // are fixed in hudController (top 118, hearts 118+330); anchoring below them avoids future 6-row /
      // 150%-scale collisions with SCORE/quota/COMBO while staying inside the same left broadcast rail.
      const railTop = 118;
      const heartsY = railTop + 330;
      y = Phaser.Math.Clamp(heartsY + strapH * 1.08, railTop + 96, H - strapH / 2 - 28);
    } else if (sc.cellH > 0 && sc.rows > 1) {
      // Revamp still gate 2026-07-06: the full portrait lower-third sat across the bottom grid row.
      // Portrait now uses a compact under-board card, below the tile field and above the timer/dock.
      strapW = Math.min(W * 0.8, 320);
      const boardBottom = sc.gridY + sc.cellH * sc.rows;
      const bottomH = Math.max(120, Math.min(150, H * 0.2));
      const dockTop = H - bottomH;
      const underBoardY = boardBottom + strapH / 2 + Math.max(2, sc.cellH * 0.04);
      const maxUnderBoardY = dockTop - strapH / 2 - Math.max(22, H * 0.035);
      y =
        underBoardY <= maxUnderBoardY
          ? underBoardY
          : Math.max(strapH / 2 + 10, sc.gridY - strapH / 2 - Math.max(6, sc.cellH * 0.08));
    }
    const railIntro = land;
    const portraitIntro = !railIntro;
    const hasAvatar = hasTex(KEYS.rivalPoseIdle);
    const yieldMs = BROADCAST.boutIntroInMs + BROADCAST.boutIntroHoldMs + BROADCAST.boutIntroOutMs + 160;
    sc.underTextYieldUntilMs = Math.max(sc.underTextYieldUntilMs, sc.time.now + yieldMs);
    sc.dismissScanTeachBubble();

    const plate = sc.add.graphics();
    // Broadcast strap: a SEMI-TRANSPARENT dark plate (live board tiles ghost through, never occluded), a
    // cyan (player) accent bar down the left, a magenta (rival) accent bar down the right — the
    // head-to-head read is carried by the two locked identity channels + the (fully opaque) labels.
    plate.fillStyle(PALETTE.void, BROADCAST.boutIntroPlateAlpha);
    plate.fillRoundedRect(-strapW / 2, -strapH / 2, strapW, strapH, 10);
    plate.lineStyle(1.5, PALETTE.lineStrong, 0.9);
    plate.strokeRoundedRect(-strapW / 2, -strapH / 2, strapW, strapH, 10);
    plate.fillStyle(PALETTE.player, 0.95);
    plate.fillRoundedRect(-strapW / 2, -strapH / 2, 4, strapH, 2);
    plate.fillStyle(PALETTE.ai, 0.95);
    plate.fillRoundedRect(strapW / 2 - 4, -strapH / 2, 4, strapH, 2);
    // S9 FIX-D (G1 AMATEUR 3a): a NEAR-OPAQUE seat behind the NAME row only. The plate body deliberately
    // stays ghost-through (STRETCH ruling: live tiles read through at 0.55), but the YOU / BYT3 glyphs
    // interleaved with same-weight tile labels through it for the whole hold — the head-to-head names
    // need a solid band. Sized to the live (text-scaled) name row; the smaller sub-line below keeps the
    // ghost-through look, so the ruling survives everywhere except directly under the names.
    const nameFs = railIntro ? scaledFont(18, 14) : Math.min(scaledFont(16, 14), Math.max(14, strapH * 0.58));
    const nameBandH = Math.round(nameFs * 1.5);
    plate.fillStyle(PALETTE.void, 0.94);
    const nameY = portraitIntro ? 0 : -4;
    plate.fillRect(-strapW / 2 + 4, nameY - nameBandH / 2, strapW - 8, nameBandH);

    const you = sc.add
      .text(-strapW / 2 + (portraitIntro ? 18 : 22), nameY, 'YOU', {
        fontFamily: FONT.display,
        fontSize: `${nameFs}px`,
        color: hex(PALETTE.player),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    const vs = sc.add
      .text(0, nameY, 'vs', {
        fontFamily: FONT.display,
        fontSize: `${railIntro ? scaledFont(11, 10) : Math.min(scaledFont(10, 9), 11)}px`,
        color: hex(PALETTE.dim),
        fontStyle: 'italic bold',
      })
      .setOrigin(0.5, 0.5);
    const rival = sc.add
      .text(strapW / 2 - (portraitIntro ? 18 : 22), nameY, RIVAL_NAME, {
        fontFamily: FONT.display,
        fontSize: `${nameFs}px`,
        color: hex(PALETTE.ai),
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5);
    // BYT3 identity avatar (reuse the accepted pose creature) just left of his name, when registered.
    const parts: Phaser.GameObjects.GameObject[] = [plate, you, vs, rival];
    if (hasAvatar) {
      const av = sc.add.image(0, nameY, tex(KEYS.rivalPoseIdle)).setOrigin(1, 0.5);
      const disp = railIntro ? 24 : Math.min(22, strapH * 0.58);
      av.setScale(disp / Math.max(av.width || 1, av.height || 1));
      av.setPosition(strapW / 2 - (portraitIntro ? 18 : 22) - rival.width - 8, nameY);
      parts.push(av);
    }
    if (railIntro) {
      // Sub-line: the tier — the "broadcast card" that names the challenge. Best-of-N context reinforces
      // the bout structure without claiming any odds. Portrait omits this line because the under-board
      // card is intentionally compact and must stay readable at phone size.
      const sub = sc.add
        .text(0, strapH / 2 - 12, `${TIER_LABEL[tier]} RIVAL · BOARD ${sc.boardIndex + 1} OF ${BROADCAST.matchBoards}`, {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(8, 8)}px`,
          color: hex(PALETTE.textDim),
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0.5);
      parts.push(sub);
    }

    const strap = sc.add.container(cx, y, parts).setDepth(11_000);
    // Slide in from the left (broadcast lower-third), hold, slide out. reduceMotion → fade only.
    if (motionAllowed()) {
      strap.setAlpha(0).setX(cx - 40);
      sc.tweens.add({ targets: strap, x: cx, alpha: 1, duration: BROADCAST.boutIntroInMs, ease: 'Back.easeOut' });
      sc.time.delayedCall(BROADCAST.boutIntroInMs + BROADCAST.boutIntroHoldMs, () => {
        if (!strap.active) return;
        sc.tweens.add({
          targets: strap,
          x: cx - 40,
          alpha: 0,
          duration: BROADCAST.boutIntroOutMs,
          ease: 'Quad.easeIn',
          onComplete: () => strap.destroy(),
        });
      });
    } else {
      strap.setAlpha(0);
      sc.tweens.add({ targets: strap, alpha: 1, duration: 200 });
      sc.time.delayedCall(BROADCAST.boutIntroInMs + BROADCAST.boutIntroHoldMs, () => {
        if (!strap.active) return;
        sc.tweens.add({ targets: strap, alpha: 0, duration: 260, onComplete: () => strap.destroy() });
      });
    }
  }
}

/** Clamp to [lo,hi] (default the unit tug-of-war range [-1,1]). */
function clampUnit(n: number, lo = -1, hi = 1): number {
  return n < lo ? lo : n > hi ? hi : n;
}
